import React, { useEffect, useState, useCallback } from "react";
import { get, post, patch, del } from "../../helpers/api_helper";
import toastr from "toastr";
import { Button, FormGroup, Input, Label, Spinner, Alert } from "reactstrap";

const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";

/**
 * Tarif çeviri penceresinde, seçili dil için hazırlanış adımlarının çevirisi.
 *
 * Adımlar Recipes_Making tablosunda Türkçe tutuluyor; çeviriler
 * Recipes_Making_Lang tablosunda (Making_ID + Lang_Code). Boş bırakılan
 * adım sitede Türkçe gösterilir.
 */
export default function RecipeStepsLang({ recipeId, lang }) {
  const [steps, setSteps] = useState([]);     // Türkçe adımlar
  const [rows, setRows] = useState({});       // { Making_ID: kayıt }
  const [draft, setDraft] = useState({});     // { Making_ID: metin }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notReady, setNotReady] = useState(false);

  const load = useCallback(async () => {
    if (!recipeId) return;
    setLoading(true);
    try {
      const res = await get(`${ODATA_BASE}/Recipes_Making?$filter=Recipes_ID eq ${recipeId}&$orderby=Recipes_Making_Sort`);
      const list = res?.value || [];
      setSteps(list);
      const map = {};
      const init = {};
      if (list.length) {
        try {
          const ids = list.map((s) => `Making_ID eq ${s.ID}`).join(" or ");
          const lr = await get(`${ODATA_BASE}/Recipes_Making_Lang?$filter=(${ids}) and Lang_Code eq '${lang}'`);
          (lr?.value || []).forEach((r) => { map[r.Making_ID] = r; init[r.Making_ID] = r.Recipes_Making_Title || ""; });
          setNotReady(false);
        } catch {
          setNotReady(true);
        }
      }
      setRows(map);
      setDraft(init);
    } finally {
      setLoading(false);
    }
  }, [recipeId, lang]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      for (const s of steps) {
        const value = (draft[s.ID] || "").trim();
        const existing = rows[s.ID];
        if (value === (existing?.Recipes_Making_Title || "").trim()) continue;
        if (existing?.ID && !value) await del(`${ODATA_BASE}/Recipes_Making_Lang(${existing.ID})`);
        else if (existing?.ID) await patch(`${ODATA_BASE}/Recipes_Making_Lang(${existing.ID})`, { Recipes_Making_Title: value });
        else if (value) await post(`${ODATA_BASE}/Recipes_Making_Lang`, { Making_ID: s.ID, Lang_Code: lang, Recipes_Making_Title: value });
      }
      toastr.success(`${lang.toUpperCase()} adım çevirileri kaydedildi.`);
      await load();
    } catch (err) {
      toastr.error("Adımlar kaydedilemedi: " + (err.response?.data?.error?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!recipeId) return null;

  return (
    <div className="mt-4 pt-3 border-top">
      <h6 className="mb-1">Hazırlanış Adımları</h6>
      <p className="text-muted font-size-12 mb-3">
        Her adımın bu dildeki karşılığını girin. Boş bırakılan adım sitede Türkçe görünür.
      </p>
      {notReady && (
        <Alert color="warning" className="py-2">
          Adım çevirileri servisi henüz aktif değil (<code>Recipes_Making_Lang</code>). Backend güncellendikten sonra kayıt yapılabilir.
        </Alert>
      )}
      {loading ? (
        <div className="py-2"><Spinner size="sm" color="warning" /> Adımlar yükleniyor…</div>
      ) : steps.length === 0 ? (
        <p className="text-muted font-size-13">Bu tarifte adım yok.</p>
      ) : (
        <>
          {steps.map((s, i) => (
            <FormGroup key={s.ID}>
              <Label className="font-size-12 text-muted mb-1">{i + 1}. {s.Recipes_Making_Title}</Label>
              <Input
                type="textarea"
                rows={2}
                value={draft[s.ID] || ""}
                placeholder={s.Recipes_Making_Title}
                onChange={(e) => setDraft((prev) => ({ ...prev, [s.ID]: e.target.value }))}
              />
            </FormGroup>
          ))}
          <Button type="button" color="warning" outline disabled={saving || notReady} onClick={save}>
            {saving ? <Spinner size="sm" className="me-1" /> : null}
            Adım çevirilerini kaydet
          </Button>
        </>
      )}
    </div>
  );
}
